/**
 * Tab Configuration Type Definitions
 *
 * Defines the structure for dynamic tab-based page layouts with support for
 * forms, tables, widgets, and related lists.
 */

// ============================================
// VISIBILITY & PERMISSIONS
// ============================================

/**
 * Visibility conditions for tabs and sections
 */
export interface VisibilityConditions {
  /** List of role IDs or names that can view this tab */
  roles?: string[];
  /** Custom JavaScript expression to evaluate visibility (e.g., "record.status === 'active'") */
  expression?: string;
  /** Field-based conditions (e.g., { fieldName: 'status', operator: 'equals', value: 'active' }) */
  fieldConditions?: FieldCondition[];
}

/**
 * Field-based visibility condition
 */
export interface FieldCondition {
  fieldName: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan' | 'isEmpty' | 'isNotEmpty';
  value?: any;
}

// ============================================
// SECTION CONFIGURATIONS
// ============================================

/**
 * Base configuration shared by all section types
 */
export interface BaseSectionConfig {
  /** Custom styling classes */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
  /** Whether section is visible by default */
  visible?: boolean;
  /** Help text or description */
  helpText?: string;
}

/**
 * Form section configuration
 * Used for displaying and editing form fields
 */
export interface FormSectionConfig extends BaseSectionConfig {
  /** Array of field definitions to display in the form */
  fields: FormField[];
  /** Form layout mode */
  layout?: 'vertical' | 'horizontal' | 'inline';
  /** Whether to show field labels */
  showLabels?: boolean;
  /** Form validation rules */
  validation?: ValidationRule[];
}

/**
 * Individual form field definition
 */
export interface FormField {
  id: string;
  fieldName: string;
  fieldLabel: string;
  fieldType: 'text' | 'email' | 'phone' | 'ssn' | 'number' | 'date' | 'datetime' | 'checkbox' | 'select' | 'multiselect' | 'textarea' | 'currency' | 'url' | 'lookup' | 'encrypted' | 'id' | 'address';
  /** Lookup/master-detail target object API name */
  lookupObject?: string;
  /** Gobodo relationship type ID for lookup/master-detail fields */
  relTypeId?: number;
  /** Field placeholder text */
  placeholder?: string;
  /** Default value */
  defaultValue?: any;
  /** Whether field is required */
  required?: boolean;
  /** Whether field is read-only */
  readonly?: boolean;
  /** Field width (e.g., 'full', 'half', '33%') */
  width?: string;
  /** Column number for multi-column layouts */
  column?: number;
  /** Display order within section */
  order: number;
  /** Options for select/picklist fields */
  options?: SelectOption[];
  /** Field-specific validation rules */
  validation?: ValidationRule[];
  /** Conditional visibility */
  visibleWhen?: FieldCondition;
}

/**
 * Select field option
 */
export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

/**
 * Form validation rule
 */
export interface ValidationRule {
  type: 'required' | 'minLength' | 'maxLength' | 'pattern' | 'custom';
  value?: any;
  message: string;
  /** Custom validation function */
  validator?: (value: any) => boolean;
}

/**
 * Table section configuration
 * Used for displaying data in tabular format
 */
export interface TableSectionConfig extends BaseSectionConfig {
  /** Data source configuration */
  dataSource: DataSource;
  /** Column definitions */
  columns: TableColumn[];
  /** Available row actions */
  actions?: TableAction[];
  /** Filter configurations */
  filters?: TableFilter[];
  /** Whether table is searchable */
  searchable?: boolean;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Pagination configuration */
  pagination?: PaginationConfig;
  /** Whether rows are selectable */
  selectable?: boolean;
  /** Row selection mode */
  selectionMode?: 'single' | 'multiple';
  /** Default sort configuration */
  defaultSort?: SortConfig;
  /** Whether to show row numbers */
  showRowNumbers?: boolean;
  /** Empty state message */
  emptyStateMessage?: string;
}

/**
 * Data source configuration for tables
 */
export interface DataSource {
  /** Type of data source */
  type: 'api' | 'static' | 'query';
  /** API endpoint (for type: 'api') */
  endpoint?: string;
  /** Static data array (for type: 'static') */
  data?: any[];
  /** Query configuration (for type: 'query') */
  query?: QueryConfig;
  /** Data transformation function */
  transform?: (data: any) => any[];
}

/**
 * Query configuration for data sources
 */
export interface QueryConfig {
  objectType: string;
  fields: string[];
  filters?: QueryFilter[];
  orderBy?: string;
  limit?: number;
}

/**
 * Query filter
 */
export interface QueryFilter {
  field: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'startsWith' | 'in' | 'greaterThan' | 'lessThan';
  value: any;
}

/**
 * Table column definition
 */
export interface TableColumn {
  id: string;
  /** Field name or data key */
  field: string;
  /** Column header label */
  label: string;
  /** Column width */
  width?: string;
  /** Whether column is sortable */
  sortable?: boolean;
  /** Whether column is resizable */
  resizable?: boolean;
  /** Cell alignment */
  align?: 'left' | 'center' | 'right';
  /** Custom cell renderer */
  render?: (value: any, row: any) => React.ReactNode;
  /** Data type for formatting */
  dataType?: 'text' | 'number' | 'date' | 'currency' | 'boolean';
  /** Format options */
  format?: FormatOptions;
}

/**
 * Format options for table columns
 */
export interface FormatOptions {
  /** Date format (e.g., 'MM/DD/YYYY') */
  dateFormat?: string;
  /** Currency symbol */
  currencySymbol?: string;
  /** Number of decimal places */
  decimals?: number;
  /** Thousand separator */
  thousandSeparator?: string;
}

/**
 * Table action definition
 */
export interface TableAction {
  id: string;
  label: string;
  icon?: string;
  /** Action type */
  type: 'button' | 'menu' | 'inline';
  /** Action handler or navigation URL */
  action: string | ((row: any) => void);
  /** Confirmation message before executing */
  confirm?: string;
  /** Whether action requires selection */
  requiresSelection?: boolean;
  /** Visibility condition */
  visibleWhen?: (row: any) => boolean;
  /** Disabled condition */
  disabledWhen?: (row: any) => boolean;
}

/**
 * Table filter configuration
 */
export interface TableFilter {
  id: string;
  field: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'dateRange' | 'number';
  options?: SelectOption[];
  defaultValue?: any;
}

/**
 * Pagination configuration
 */
export interface PaginationConfig {
  enabled: boolean;
  pageSize: number;
  pageSizeOptions?: number[];
  showTotal?: boolean;
}

/**
 * Sort configuration
 */
export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * Widget section configuration
 * Used for custom React components
 */
export interface WidgetSectionConfig extends BaseSectionConfig {
  /** Name of the React component to render */
  componentName: string;
  /** Props to pass to the component */
  props?: Record<string, any>;
  /** Data binding configuration */
  dataBinding?: DataBinding;
  /** Refresh interval in milliseconds */
  refreshInterval?: number;
}

/**
 * Data binding for widgets
 */
export interface DataBinding {
  source: 'record' | 'api' | 'context';
  /** Field mapping (source field -> component prop) */
  fieldMap?: Record<string, string>;
  /** API endpoint (for source: 'api') */
  endpoint?: string;
}

/**
 * Activity section configuration
 * Used for displaying activity timeline with modals
 */
export interface ActivitySectionConfig extends BaseSectionConfig {
  /** Record type for activity context (client, contact, etc.) */
  recordType?: string;
}

/**
 * Related list section configuration
 * Used for displaying related records
 */
export interface RelatedListSectionConfig extends BaseSectionConfig {
  /** Relationship definition */
  relationship: Relationship;
  /** Related object type */
  objectType: string;
  /** Fields to display */
  fields: string[];
  /** Actions available on related records */
  actions?: RelatedListAction[];
  /** Whether to allow inline editing */
  inlineEdit?: boolean;
  /** Whether to allow creating new related records */
  allowCreate?: boolean;
  /** Create button label */
  createButtonLabel?: string;
  /** Maximum number of records to display */
  limit?: number;
  /** Default sort order */
  sortBy?: string;
  /** Sort direction */
  sortDirection?: 'asc' | 'desc';
}

/**
 * Relationship definition
 */
export interface Relationship {
  /** Relationship type */
  type: 'oneToMany' | 'manyToOne' | 'manyToMany';
  /** Foreign key field name */
  foreignKey: string;
  /** Related field name (for manyToMany) */
  relatedField?: string;
  /** Junction table (for manyToMany) */
  junctionTable?: string;
}

/**
 * Related list action
 */
export interface RelatedListAction {
  id: string;
  label: string;
  icon?: string;
  action: string | ((record: any) => void);
  confirm?: string;
}

// ============================================
// TAB SECTION
// ============================================

/**
 * Tab section definition
 * Represents a section within a tab (form, table, widget, or related list)
 */
export interface TabSection {
  /** Unique section identifier */
  id: string;
  /** Section type */
  type: 'form' | 'table' | 'widget' | 'activity' | 'related-list';
  /** Internal section name (used in code) */
  name: string;
  /** Display label */
  label: string;
  /** Display order within tab */
  order: number;
  /** Whether section can be collapsed */
  collapsible: boolean;
  /** Whether section is collapsed by default */
  collapsed?: boolean;
  /** Number of columns for layout */
  columns: number;
  /** Type-specific configuration */
  config: FormSectionConfig | TableSectionConfig | WidgetSectionConfig | ActivitySectionConfig | RelatedListSectionConfig;
  /** Visibility conditions */
  visibility?: VisibilityConditions;
  /** Custom CSS class names */
  className?: string;
  /** Help text or description */
  description?: string;
}

// ============================================
// TAB CONFIGURATION
// ============================================

/**
 * Tab configuration definition
 * Represents a complete tab with its sections and settings
 */
export interface TabConfiguration {
  /** Unique tab identifier */
  id: string;
  /** Page this tab belongs to */
  pageId: string;
  /** Tab display label */
  label: string;
  /** Icon name (from icon library) */
  icon?: string;
  /** Display order among tabs */
  order: number;
  /** Whether this is the default active tab */
  isDefault: boolean;
  /** Visibility conditions */
  visibility?: VisibilityConditions;
  /** Sections contained in this tab */
  sections: TabSection[];
  /** Tab-level permissions */
  permissions?: TabPermissions;
  /** Whether tab content should be lazy-loaded */
  lazyLoad?: boolean;
  /** Custom CSS class names */
  className?: string;
  /** Tab description or help text */
  description?: string;
  /** Metadata for system use */
  metadata?: Record<string, any>;
  /** Created timestamp */
  createdAt?: string;
  /** Last updated timestamp */
  updatedAt?: string;
  /** Created by user */
  createdBy?: string;
  /** Last updated by user */
  updatedBy?: string;
}

/**
 * Tab-level permissions
 */
export interface TabPermissions {
  /** Roles that can view this tab */
  canView?: string[];
  /** Roles that can edit content in this tab */
  canEdit?: string[];
  /** Roles that can delete content in this tab */
  canDelete?: string[];
}

// ============================================
// APPLICATION PAGE CONFIGURATION
// ============================================

/**
 * Application page configuration
 * Represents a complete page with multiple tabs
 */
export interface ApplicationPageConfig {
  /** Unique page identifier */
  id: string;
  /** Application this page belongs to */
  applicationName: string;
  /** Page name (used in routing) */
  pageName: string;
  /** Page type/purpose */
  pageType: 'detail' | 'list' | 'dashboard' | 'custom';
  /** Page display title */
  title?: string;
  /** Page description */
  description?: string;
  /** Tabs configuration */
  tabs: TabConfiguration[];
  /** Default tab ID to show */
  defaultTabId?: string;
  /** Page-level permissions */
  permissions?: PagePermissions;
  /** Page layout mode */
  layout?: 'standard' | 'sidebar' | 'fullwidth';
  /** Whether to show page header */
  showHeader?: boolean;
  /** Custom page actions */
  actions?: PageAction[];
  /** Metadata for system use */
  metadata?: Record<string, any>;
  /** Created timestamp */
  createdAt?: string;
  /** Last updated timestamp */
  updatedAt?: string;
  /** Created by user */
  createdBy?: string;
  /** Last updated by user */
  updatedBy?: string;
  /** Version number */
  version?: number;
  /** Whether page is active */
  isActive?: boolean;
  /** Whether page is published */
  isPublished?: boolean;
}

/**
 * Page-level permissions
 */
export interface PagePermissions {
  /** Roles that can view this page */
  canView?: string[];
  /** Roles that can edit this page configuration */
  canEdit?: string[];
  /** Roles that can delete this page */
  canDelete?: string[];
}

/**
 * Page action definition
 */
export interface PageAction {
  id: string;
  label: string;
  icon?: string;
  type: 'primary' | 'secondary' | 'danger';
  action: string | (() => void);
  confirm?: string;
  /** Visibility condition */
  visibleWhen?: () => boolean;
  /** Disabled condition */
  disabledWhen?: () => boolean;
}

// ============================================
// UTILITY TYPES
// ============================================

/**
 * Type guard to check if a section is a form section
 */
export function isFormSection(section: TabSection): section is TabSection & { config: FormSectionConfig } {
  return section.type === 'form';
}

/**
 * Type guard to check if a section is a table section
 */
export function isTableSection(section: TabSection): section is TabSection & { config: TableSectionConfig } {
  return section.type === 'table';
}

/**
 * Type guard to check if a section is a widget section
 */
export function isWidgetSection(section: TabSection): section is TabSection & { config: WidgetSectionConfig } {
  return section.type === 'widget';
}

/**
 * Type guard to check if a section is a related list section
 */
export function isRelatedListSection(section: TabSection): section is TabSection & { config: RelatedListSectionConfig } {
  return section.type === 'related-list';
}

/**
 * Section type union for discriminated unions
 */
export type SectionConfig = FormSectionConfig | TableSectionConfig | WidgetSectionConfig | ActivitySectionConfig | RelatedListSectionConfig;

/**
 * Tab section type union
 */
export type TabSectionType = 'form' | 'table' | 'widget' | 'activity' | 'related-list';

/**
 * Page type union
 */
export type PageType = 'detail' | 'list' | 'dashboard' | 'custom';

/**
 * Visibility operator type
 */
export type VisibilityOperator = 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan' | 'isEmpty' | 'isNotEmpty';
